const LS_PRINTER_KEY = "tiempos_bluetooth_printer";

interface PrinterInfo {
  id: string;
  name: string;
  serviceUuid?: string;
  characteristicUuid?: string;
}

interface CaracteristicaEscritura {
  uuid?: string;
  properties: { write?: boolean; writeWithoutResponse?: boolean };
  writeValue: (value: BufferSource) => Promise<void>;
}

interface ServicioGatt {
  uuid?: string;
  getCharacteristic: (uuid: string) => Promise<CaracteristicaEscritura>;
  getCharacteristics: () => Promise<CaracteristicaEscritura[]>;
}

interface ServidorGatt {
  getPrimaryService: (uuid: string) => Promise<ServicioGatt>;
  getPrimaryServices: () => Promise<ServicioGatt[]>;
}

const CANDIDATE_SERVICE_UUIDS = [
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455"
];

const CANDIDATE_CHARACTERISTICS = [
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "49535343-1e4d-4bd9-ba61-23c647249616",
  "00002af1-0000-1000-8000-00805f9b34fb"
];

function toEscPosBytes(text: string, ancho: 58 | 80): Uint8Array {
  const encoder = new TextEncoder();
  const bytes: number[] = [];

  // Init + codepage latin + alignment center header
  bytes.push(0x1b, 0x40);
  bytes.push(0x1b, 0x74, 0x10);

  const lineLength = ancho === 58 ? 32 : 48;
  const lines = text.split("\n").map((line) => line.trimEnd());

  for (const line of lines) {
    if (!line) {
      bytes.push(0x0a);
      continue;
    }
    if (line.length <= lineLength) {
      const lineBytes = Array.from(encoder.encode(line));
      bytes.push(...lineBytes, 0x0a);
      continue;
    }

    for (let i = 0; i < line.length; i += lineLength) {
      const part = line.slice(i, i + lineLength);
      bytes.push(...Array.from(encoder.encode(part)), 0x0a);
    }
  }

  // Feed + cut
  bytes.push(0x0a, 0x0a, 0x0a);
  bytes.push(0x1d, 0x56, 0x00);

  return new Uint8Array(bytes);
}

async function resolveWritableCharacteristic(server: ServidorGatt): Promise<{ serviceUuid?: string; characteristic: CaracteristicaEscritura; characteristicUuid?: string }> {
  for (const serviceUuid of CANDIDATE_SERVICE_UUIDS) {
    try {
      const service = await server.getPrimaryService(serviceUuid);
      for (const characteristicUuid of CANDIDATE_CHARACTERISTICS) {
        try {
          const characteristic = await service.getCharacteristic(characteristicUuid);
          if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
            return { serviceUuid, characteristic, characteristicUuid };
          }
        } catch {
          // try next characteristic
        }
      }
      const chars = await service.getCharacteristics();
      const writable = chars.find((c: CaracteristicaEscritura) => c.properties.write || c.properties.writeWithoutResponse);
      if (writable) {
        return { serviceUuid, characteristic: writable, characteristicUuid: writable.uuid };
      }
    } catch {
      // try next service
    }
  }

  const services = await server.getPrimaryServices();
  for (const service of services) {
    const chars = await service.getCharacteristics();
    const writable = chars.find((c: CaracteristicaEscritura) => c.properties.write || c.properties.writeWithoutResponse);
    if (writable) {
      return { serviceUuid: service.uuid, characteristic: writable, characteristicUuid: writable.uuid };
    }
  }

  throw new Error("No se encontró una característica Bluetooth de escritura para la impresora.");
}

export function getSavedBluetoothPrinter(): PrinterInfo | null {
  try {
    const raw = localStorage.getItem(LS_PRINTER_KEY);
    return raw ? (JSON.parse(raw) as PrinterInfo) : null;
  } catch {
    return null;
  }
}

export async function connectBluetoothPrinter(): Promise<PrinterInfo> {
  const bluetooth = (navigator as any).bluetooth;
  if (!bluetooth) {
    throw new Error("Este navegador no soporta Web Bluetooth.");
  }

  const device = await bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: CANDIDATE_SERVICE_UUIDS
  });

  if (!device.gatt) {
    throw new Error("El dispositivo no expone GATT para impresión BLE.");
  }

  const server = await device.gatt.connect();
  const { serviceUuid, characteristicUuid } = await resolveWritableCharacteristic(server);

  const info: PrinterInfo = {
    id: device.id,
    name: device.name || "Impresora POS",
    serviceUuid,
    characteristicUuid
  };
  localStorage.setItem(LS_PRINTER_KEY, JSON.stringify(info));

  return info;
}

export async function printTicketBluetooth(ticketText: string, options?: { ancho?: 58 | 80 }): Promise<void> {
  const bluetooth = (navigator as any).bluetooth;
  if (!bluetooth) {
    throw new Error("Web Bluetooth no está disponible en este dispositivo.");
  }

  const device = await bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: CANDIDATE_SERVICE_UUIDS
  });

  if (!device.gatt) {
    throw new Error("No se pudo abrir conexión GATT con la impresora.");
  }

  const server = await device.gatt.connect();
  const { characteristic } = await resolveWritableCharacteristic(server);
  const bytes = toEscPosBytes(ticketText, options?.ancho || 80);

  const chunkSize = 180;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    await characteristic.writeValue(chunk);
  }

  if (device.gatt.connected) {
    device.gatt.disconnect();
  }
}

export function payloadToTicketText(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number }>;
}): string {
  const lines: string[] = [];
  lines.push("TIEMPOS V2");
  lines.push("TICKET " + payload.ticketCode);
  lines.push("-------------------------------");
  lines.push("Sorteo: " + payload.sorteoNombre);
  if (payload.fecha) {
    lines.push("Fecha: " + payload.fecha);
  }
  lines.push("-------------------------------");
  for (const jugada of payload.jugadas) {
    const nums = jugada.num2 ? `${jugada.num1}-${jugada.num2}` : jugada.num1;
    lines.push(`${nums}  C${Math.trunc(jugada.monto)}`);
  }
  lines.push("-------------------------------");
  lines.push("TOTAL: C" + Math.trunc(payload.total).toLocaleString("es-CR"));
  lines.push("\n\n");
  return lines.join("\n");
}